import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

// @mentions in a team note (Gary, 19 Oct): typing @ lists who can be alerted on this chat (its owner and head office,
// the same people messaging-send alerts), narrowing as you type; a tap puts "@Name " in the note, so a misspelt name
// can't slip through.

type Person = { id: string; name: string };
const cache = new Map<string, Person[]>();

/** The @word being typed at the end of the note, or null. */
function typingMention(text: string): string | null {
  const m = /(?:^|\s)@([\p{L}'-]*)$/u.exec(text);
  return m ? m[1]!.toLowerCase() : null;
}

export function MentionPicker({ chatId, text, onPick }: { chatId: string; text: string; onPick: (text: string) => void }) {
  const [people, setPeople] = useState<Person[] | null>(() => cache.get(chatId) ?? null);
  const query = typingMention(text);

  useEffect(() => {
    if (query === null || cache.has(chatId)) return;
    let gone = false;
    void supabase.functions.invoke('messaging-send', { body: { action: 'mentionable', conversation_id: chatId } }).then(({ data }) => {
      const list = ((data as { people?: Person[] } | null)?.people ?? []);
      cache.set(chatId, list);
      if (!gone) setPeople(list);
    });
    return () => { gone = true; };
  }, [chatId, query]);

  if (query === null || !people) return null;
  const shown = people.filter((p) => p.name.toLowerCase().startsWith(query) || p.name.toLowerCase().includes(` ${query}`));
  if (!shown.length) return <div className="chats-chips" role="status"><span className="chats-chip">Nobody here called @{query}</span></div>;
  return (
    <div className="chats-chips" role="listbox" aria-label="Alert someone">
      {shown.map((p) => (
        <button key={p.id} type="button" role="option" aria-selected={false} className="chats-chip"
          onClick={() => onPick(text.replace(/@([\p{L}'-]*)$/u, `@${p.name.split(' ')[0]} `))}>
          {p.name}
        </button>
      ))}
    </div>
  );
}
